import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  Clock,
  ArrowRight,
  MapPin,
  MessageSquare,
  Facebook,
  Instagram,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { siteConfig } from "@/lib/site-config";
import { services } from "@/lib/services-data";
import { locations } from "@/lib/locations-data";

const quickLinks = [
  { label: "Services", href: "/services" },
  { label: "Locations", href: "/locations" },
  { label: "Gallery", href: "/gallery" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
];

const TikTokIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 1 1 .76-5.07v-3.15a5.72 5.72 0 0 0-.76-.05A5.69 5.69 0 1 0 15.54 15V8.99a7.35 7.35 0 0 0 4.3 1.38V7.28a4.29 4.29 0 0 1-3.24-1.46Z" />
  </svg>
);

/** Renders only the profiles the client actually has. */
const SocialLinks = () => {
  const { instagram, facebook, tiktok, yelp } = siteConfig.social;
  const links = [
    { href: facebook, label: "Facebook", Icon: Facebook },
    { href: instagram, label: "Instagram", Icon: Instagram },
    { href: tiktok, label: "TikTok", Icon: TikTokIcon },
    { href: yelp, label: "Yelp", Icon: Star },
  ].filter((l): l is { href: string; label: string; Icon: typeof Facebook } => Boolean(l.href));

  if (!links.length) return null;

  return (
    <div className="mt-6">
      <h4 className="font-heading text-sm tracking-tight mb-3 text-white/90 font-bold uppercase">
        Follow Us
      </h4>
      <div className="flex items-center gap-3">
        {links.map(({ href, label, Icon }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${siteConfig.shortName} on ${label}`}
            className="h-10 w-10 rounded-xl bg-white/10 hover:bg-secondary hover:text-secondary-foreground text-white flex items-center justify-center transition-colors"
          >
            <Icon className="h-5 w-5" />
          </a>
        ))}
      </div>
    </div>
  );
};

const Footer = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <footer className="bg-[#070d12] text-white">
      {/* CTA banner */}
      <div className="bg-primary">
        <div className="container-max px-4 py-12 md:py-14">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-primary-foreground font-bold tracking-tight">
                Ready to Start Your Project?
              </h2>
              <p className="text-primary-foreground/85 text-sm md:text-base mt-1">
                Free written estimates across the Denver metro &amp; Northern Colorado · Se habla
                español.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="secondary"
                size="lg"
                onClick={openQuoteForm}
                className="gap-2 rounded-xl font-bold"
              >
                Get Free Estimate <ArrowRight className="h-5 w-5" />
              </Button>
              <Button
                asChild
                size="lg"
                className="gap-2 rounded-xl font-bold bg-white text-primary hover:bg-white/90"
              >
                <a href={siteConfig.phoneHref}>
                  <Phone className="h-4 w-4" /> {siteConfig.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-max section-padding">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Logo + one-liner */}
          <div>
            <div className="inline-flex items-center justify-center bg-white rounded-2xl p-2 shadow-md mb-5">
              <img
                src={siteConfig.logo}
                alt={`${siteConfig.name} logo`}
                className="h-20 w-auto rounded-xl"
                width={218}
                height={240}
                loading="lazy"
              />
            </div>
            <h3 className="font-heading text-xl text-white font-bold mb-2 tracking-tight">
              {siteConfig.name}
            </h3>
            <p className="text-white/70 text-sm leading-relaxed">
              Family-run painting, waterproofing, drywall, tile, and remodeling contractors based in
              Highlands Ranch — quality you can trust, in English or Spanish.
            </p>
            <div className="mt-4 bg-secondary/15 border border-secondary/30 rounded-xl px-4 py-3">
              <p className="text-white font-semibold text-sm">🎁 {siteConfig.discount.label}</p>
              <p className="text-white/70 text-xs mt-0.5">{siteConfig.discount.referral}</p>
            </div>

            <SocialLinks />
          </div>

          {/* Quick links + services */}
          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">
              Quick Links
            </h3>
            <ul className="space-y-2.5 mb-6">
              {quickLinks.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="footer-link-underline text-white/70 hover:text-white text-sm transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>

            <h3 className="font-heading text-lg tracking-tight mb-3 text-white font-bold">
              Services
            </h3>
            <ul className="space-y-2">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="footer-link-underline text-white/70 hover:text-white text-sm transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service areas */}
          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">
              Service Areas
            </h3>
            <ul className="grid grid-cols-2 gap-x-2 gap-y-2 text-sm">
              {locations.slice(0, 12).map((loc) => (
                <li key={loc.slug}>
                  <Link
                    to={`/locations/${loc.slug}`}
                    className="footer-link-underline text-white/70 hover:text-white transition-colors"
                  >
                    {loc.name}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/locations"
              className="mt-3 inline-block footer-link-underline text-secondary hover:text-secondary/80 text-sm font-semibold transition-colors"
            >
              All {locations.length} Locations →
            </Link>
          </div>

          {/* Contact + hours */}
          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">
              Contact
            </h3>
            <ul className="space-y-3 text-sm text-white/75">
              <li className="flex items-start gap-2">
                <Phone className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <a
                  href={siteConfig.phoneHref}
                  className="hover:text-white transition-colors inline-block py-1 -my-1"
                >
                  {siteConfig.phone} <span className="text-white/50">(calls)</span>
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MessageSquare className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <a
                  href={siteConfig.smsHref}
                  className="hover:text-white transition-colors inline-block py-1 -my-1"
                >
                  {siteConfig.smsPhone} <span className="text-white/50">(text)</span>
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <a
                  href={siteConfig.emailHref}
                  className="hover:text-white transition-colors break-all inline-block py-1 -my-1"
                >
                  {siteConfig.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <address className="not-italic">
                  {siteConfig.address.street}
                  <br />
                  {siteConfig.address.city}, {siteConfig.address.state}{" "}
                  {siteConfig.address.postalCode}
                </address>
              </li>
            </ul>

            <h3 className="font-heading text-lg tracking-tight mt-6 mb-3 text-white font-bold">
              Business Hours
            </h3>
            <ul className="space-y-2 text-sm text-white/75">
              <li className="flex items-start gap-2">
                <Clock className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <div>
                  <div>{siteConfig.hours.days}</div>
                  <div className="text-white/90">{siteConfig.hours.time}</div>
                  <div className="text-white/60 text-xs mt-1">Saturday — by appointment</div>
                  <div className="text-white/60 text-xs">Sunday — closed</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/75 text-xs">
            © {new Date().getFullYear()} {siteConfig.legalName}. All rights reserved.
          </p>
          <p className="text-white/75 text-xs">
            Painting · Waterproofing · Remodeling · Drywall · Tile — Colorado
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
